import { useEffect, useState, useRef } from "react";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";

export default function HeroCarousel({ slides, headline, subhead, children }) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef();

  useEffect(() => {
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (paused || prefersReduced) return undefined;
    timer.current = setInterval(() => setI((v) => (v + 1) % slides.length), 6000);
    return () => clearInterval(timer.current);
  }, [slides.length, paused]);

  const go = (n) => {
    clearInterval(timer.current);
    setI((n + slides.length) % slides.length);
    if (!paused) {
      timer.current = setInterval(() => setI((v) => (v + 1) % slides.length), 6000);
    }
  };

  return (
    <section
      data-testid="hero-carousel"
      data-hero-dark="true"
      className="relative h-[92vh] min-h-[560px] w-full overflow-hidden bg-[#1A1A1A]"
      aria-roledescription="carousel"
      aria-label="Property photos"
    >
      {slides.map((s, idx) => (
        <div
          key={s.src}
          aria-hidden="true"
          className={`absolute inset-0 transition-opacity duration-1000 ${
            idx === i ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={s.src}
            alt=""
            aria-hidden="true"
            className={`w-full h-full object-cover ${idx === i ? "wb-ken" : ""}`}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/70" />
        </div>
      ))}

      {/* SR-only live region announces current slide subject to assistive tech */}
      <div className="sr-only" aria-live="polite" aria-atomic="true" data-testid="hero-live-region">
        {slides[i]?.alt || `Slide ${i + 1} of ${slides.length}`}
      </div>

      <div className="relative z-10 h-full wb-container flex flex-col justify-end pb-24 md:pb-32">
        <div className="max-w-3xl">
          <div className="wb-eyebrow text-[#FFC8B0] wb-fade-up">{slides[i].eyebrow || "Westbridge Inn & Suites"}</div>
          <h1
            className="wb-h1 text-white mt-4 wb-fade-up-delay-1"
            style={{ textShadow: "0 4px 24px rgba(0,0,0,0.4)" }}
          >
            {headline}
          </h1>
          <p className="wb-body text-white/90 mt-5 text-lg sm:text-xl max-w-2xl wb-fade-up-delay-2">
            {subhead}
          </p>
          <div className="mt-8 flex flex-wrap gap-3 wb-fade-up-delay-3">{children}</div>
        </div>
      </div>

      <div className="absolute bottom-6 right-6 md:bottom-10 md:right-10 z-20 flex items-center gap-2">
        <button
          onClick={() => setPaused((p) => !p)}
          className="p-3 rounded-full bg-white/10 hover:bg-white/25 text-white backdrop-blur-md border border-white/20"
          aria-label={paused ? "Play carousel" : "Pause carousel"}
          aria-pressed={paused}
          data-testid="hero-pause"
        >
          {paused ? <Play className="h-5 w-5" aria-hidden="true" /> : <Pause className="h-5 w-5" aria-hidden="true" />}
        </button>
        <button
          onClick={() => go(i - 1)}
          className="p-3 rounded-full bg-white/10 hover:bg-white/25 text-white backdrop-blur-md border border-white/20"
          aria-label="Previous slide"
          data-testid="hero-prev"
        >
          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <button
          onClick={() => go(i + 1)}
          className="p-3 rounded-full bg-white/10 hover:bg-white/25 text-white backdrop-blur-md border border-white/20"
          aria-label="Next slide"
          data-testid="hero-next"
        >
          <ChevronRight className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 md:left-auto md:right-32 md:translate-x-0 z-20 flex gap-1.5">
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => go(idx)}
            aria-label={`Go to slide ${idx + 1}`}
            data-testid={`hero-dot-${idx}`}
            className={`h-1.5 rounded-full transition-all ${
              idx === i ? "w-10 bg-[#E85D26]" : "w-4 bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}
